import React, { useState, useEffect, useRef } from 'react';
import { LinkWithToken as Link } from '../LinkWithToken';
import transcript_pb, { Transcript } from '@gen/proto/lr/transcript/v1alpha1/transcript_pb';
import { Card, List, Input, Form, Tag, Typography, Space } from 'antd';
import {
  ClockCircleOutlined,
  GlobalOutlined,
  StepBackwardOutlined,
  FastForwardOutlined,
  VideoCameraOutlined as FileVideoOutlined,
  AudioOutlined as FileAudioOutlined,
  PlusOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { timestampToString, durationToString, languageCodeToFriendly } from '../helpers';
import { Duration } from 'google-protobuf/google/protobuf/duration_pb';
import './TranscriptMetadataDisplay.css';
import { deepEqual } from '../utils';

const { TextArea } = Input;
const { Paragraph, Title } = Typography;

const RecordingDurationListItem: React.FC<{ duration?: Duration.AsObject }> = (props) => {
  return (
    <>
      {props.duration ? (
        <List.Item>
          <List.Item.Meta avatar={<ClockCircleOutlined />} title={durationToString(props.duration)} />
        </List.Item>
      ) : null}
    </>
  );
};

const VersionListItem: React.FC<{
  transcriptName?: string;
  version?: Transcript.Version.AsObject;
  editable: boolean;
}> = (props) => {
  return (
    <>
      {props.version && props.transcriptName ? (
        <List.Item title={`Útgáfa ${props.version ? props.version.name : 'óþekkt'}`}>
          <List.Item.Meta
            avatar={<HistoryOutlined />}
            title={`Vistað: ${timestampToString(props.version.creationTime)}`}
          />
        </List.Item>
      ) : null}
    </>
  );
};

const ParentVersionListItem: React.FC<{
  transcriptName?: string;
  version?: Transcript.Version.AsObject;
  editable: boolean;
}> = (props) => {
  return (
    <>
      {props.transcriptName && props.version && props.version.parent ? (
        <List.Item>
          <List.Item.Meta
            avatar={<StepBackwardOutlined />}
            title={
              <Link
                to={`/${props.transcriptName}/version/${props.version.parent}`}
                style={!props.editable ? { pointerEvents: 'none', color: 'rgba(0, 0, 0, 0.45)' } : {}}
              >
                Eldri útgáfa
              </Link>
            }
          />
        </List.Item>
      ) : null}
    </>
  );
};

const NewestVersionListItem: React.FC<{
  transcriptName?: string;
  version?: Transcript.Version.AsObject;
  editable: boolean;
}> = (props) => {
  return (
    <>
      {props.transcriptName ? (
        <List.Item>
          <List.Item.Meta
            avatar={<FastForwardOutlined />}
            title={
              <Link
                to={`/${props.transcriptName}`}
                style={!props.editable ? { pointerEvents: 'none', color: 'rgba(0, 0, 0, 0.45)' } : {}}
              >
                Nýjasta útgáfa
              </Link>
            }
          />
        </List.Item>
      ) : null}
    </>
  );
};

type LabelsProps = {
  labels: string[];
  editable?: boolean;
  onRemove?: (label: string) => void;
};

function Labels(props: LabelsProps) {
  return (
    <Space size={[0, 8]} wrap>
      {props.labels.map((label) => (
        <Tag
          color='blue'
          key={label}
          closable={props.editable} // Use built-in closable
          onClose={() => props.onRemove && props.onRemove(label)} // Use onClose
        >
          {label}
        </Tag>
      ))}
    </Space>
  );
}

type EditableLabelProps = {
  onFinish?: (value: string) => void;
};

function EditableLabel(props: EditableLabelProps) {
  const [value, setValue] = useState<string>('');
  const [editing, setEditing] = useState<boolean>(false);
  const inputRef = useRef<any>(null); // Ref for AntD Input

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  function finish() {
    props.onFinish && props.onFinish(value);
    setValue('');
    setEditing(false);
  }

  if (editing) {
    return (
      <Input
        ref={inputRef}
        type='text'
        size='small'
        style={{ width: 100 }}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={finish}
        onPressEnter={finish}
      />
    );
  }

  return (
    <Tag onClick={() => setEditing(true)} style={{ borderStyle: 'dashed', cursor: 'pointer' }}>
      <PlusOutlined /> Bæta við
    </Tag>
  );
}

export type TranscriptMetadataDisplayProps = {
  name?: string;
  metadata: transcript_pb.TranscriptMetadata.AsObject;
  version?: transcript_pb.Transcript.Version.AsObject;
  editable?: boolean;
  onChange?: (value: transcript_pb.TranscriptMetadata.AsObject) => void;
};

class TranscriptMetadataDisplay extends React.Component<TranscriptMetadataDisplayProps> {
  shouldComponentUpdate(prevProps: TranscriptMetadataDisplayProps) {
    return (
      this.props.name !== prevProps.name ||
      this.props.editable !== prevProps.editable ||
      !deepEqual(this.props.metadata, prevProps.metadata) ||
      !deepEqual(this.props.version, prevProps.version)
    );
  }

  render() {
    const icon = (() => {
      switch (this.props.metadata.fileType) {
        case transcript_pb.TranscriptMetadata.FileType.VIDEO:
          return <FileVideoOutlined />;
        default:
          return <FileAudioOutlined />;
      }
    })();

    const extraList = (
      <List size='small'>
        <List.Item>
          <List.Item.Meta
            avatar={icon}
            title={
              this.props.metadata.originalFilename
                ? this.props.metadata.originalFilename
                : this.props.metadata.originalUri
            }
          />
        </List.Item>
        <VersionListItem
          editable={!!this.props.editable}
          transcriptName={this.props.name}
          version={this.props.version}
        />
        <NewestVersionListItem
          editable={!!this.props.editable}
          transcriptName={this.props.name}
          version={this.props.version}
        />
        <ParentVersionListItem
          editable={!!this.props.editable}
          transcriptName={this.props.name}
          version={this.props.version}
        />
        <List.Item>
          <List.Item.Meta
            avatar={<GlobalOutlined />}
            title={languageCodeToFriendly(this.props.metadata.languageCode)}
          />
        </List.Item>
        <RecordingDurationListItem duration={this.props.metadata.recordingDuration} />
      </List>
    );

    return (
      <Card
        title={
          this.props.editable ? (
            <Form>
              <Input
                required
                placeholder='Titill...'
                type='text'
                value={this.props.metadata.subject}
                onChange={(e) => {
                  if (!this.props.onChange) return;
                  this.props.onChange({
                    ...this.props.metadata,
                    subject: e.target.value as string,
                  });
                }}
              />
            </Form>
          ) : (
            <Title level={4}>{this.props.metadata.subject}</Title>
          )
        }
      >
        {this.props.editable ? (
          <Form>
            <TextArea
              placeholder='Lýsing'
              value={this.props.metadata.description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                if (!this.props.onChange) return;
                this.props.onChange({
                  ...this.props.metadata,
                  description: e.target.value as string,
                });
              }}
              autoSize={{ minRows: 2, maxRows: 6 }}
            />
          </Form>
        ) : (
          <Paragraph>{this.props.metadata.description}</Paragraph>
        )}

        <div style={{ paddingTop: '16px', marginTop: '16px', borderTop: '1px solid #f0f0f0' }}>
          <Labels
            labels={this.props.metadata.keywordsList}
            editable={this.props.editable}
            onRemove={(label) => {
              if (!this.props.onChange) return;
              this.props.onChange({
                ...this.props.metadata,
                keywordsList: this.props.metadata.keywordsList.filter((l) => l !== label),
              });
            }}
          />
          {this.props.editable ? (
            <EditableLabel
              onFinish={(label) => {
                const newKeyword = label.replace(',', '').trim();
                if (
                  !this.props.onChange ||
                  newKeyword === '' ||
                  this.props.metadata.keywordsList.indexOf(newKeyword) !== -1
                ) {
                  return;
                }

                this.props.onChange({
                  ...this.props.metadata,
                  keywordsList: [...this.props.metadata.keywordsList, newKeyword],
                });
              }}
            />
          ) : null}
        </div>

        <div style={{ marginTop: '16px', borderTop: '1px solid #f0f0f0' }}>{extraList}</div>
      </Card>
    );
  }
}

export default React.memo(TranscriptMetadataDisplay);
export { RecordingDurationListItem };
